import React, { useRef, useContext } from 'react'
import styled from '@emotion/styled'
import { message } from 'antd'
import { usePathname } from 'next/navigation'
import { LinkContext } from 'app/components/providers/linkProvider'
import { ReactComponent as FileIcon } from '../../images/svg/file.svg'
import { ReactComponent as CheckCircleIcon } from '../../images/svg/check-circle.svg'

import { getUrlSearchString } from '../utils'
import { maxFileSize, useUploadResumeContext } from './context'
import UploadResumeWrap from './Wrap'
import { Gtag } from 'helpers/gtagEvent'
import { Button } from 'app/(new-pages)/components/Button/pc/index.pc'
import { RouterContext } from '../routerProvider'

const UploadSuccess = (props: {
  lang: any
  userDetail: any
  getUserInfo: () => Promise<any>
  onOk: (data: any) => void
}) => {
  const { getUserInfo, userDetail, lang = {} } = props
  const profile = lang.profile || {}
  const inputRef = useRef(null)
  const { fileData, uploadResumeFile } = useUploadResumeContext()
  const { push } = useContext(LinkContext)
  const pathname = usePathname()

  const onChange = (event) => {
    const file = event.target.files[0]
    if (!file) {
      return
    }
    if (file.size > maxFileSize) {
      message.error(profile?.uploadResumeModal?.errorTip)
      return false
    }
    const resumeId = userDetail?.resume?.id || fileData?.data?.id
    uploadResumeFile({ file, resumeId: resumeId })
  }

  const handleUpload = () => {
    inputRef.current.click()
  }

  const handSubmit = () => {
    Gtag('register_upload_resume')
    setTimeout(() => {
      const searchWords = getUrlSearchString(1)
      getUserInfo && getUserInfo()
      push(`${pathname}/thank-you/${searchWords}`)
    }, 200)
  }

  return (
    <UploadResumeWrap
      {...props}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CheckCircleIcon />
          <span>{profile?.uploadResumeModal?.uploadSuccess}</span>
        </div>
      }
      footer={
        <div
          className='footerWrapper'
          style={{ display: 'flex', gap: '12px', justifyContent: 'center', paddingBottom: '8px' }}
        >
          <input
            type='file'
            accept='.pdf, .doc, .docx'
            ref={inputRef}
            onChange={onChange}
            style={{ display: 'none' }}
            hidden
          />
          <Button
            variant='whiteBoxShadowBtn'
            btnSize='size44'
            className='re-upload'
            onClick={handleUpload}
          >
            {profile?.uploadResumeModal?.reUploadBtn}
          </Button>
          <Button variant='primaryBtn' btnSize='size44' className='confirm' onClick={handSubmit}>
            {profile?.confirm}
          </Button>
        </div>
      }
    >
      <ContentWrap>
        <div className='modal-content'>
          <div className='file-info'>
            {fileData?.data?.name?.indexOf('word') > -1 ? WordPdf : PdfSvg}
            <span className='file-name'>{fileData?.data?.name || ''}</span>
          </div>
        </div>
      </ContentWrap>
    </UploadResumeWrap>
  )
}

const media = {
  sm: `@media (max-width: 576px)`,
  md: `@media (min-width: 800px)`,
  msd: `@media (max-width: 799px)`
}

const ContentWrap = styled.div`
  .modal-content {
    text-align: center;
  }

  .file-info {
    display: flex;
    padding: 60px 24px 12px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    align-self: stretch;
    ${media.msd} {
      padding: 48px 0 0 0;
    }
    .file-name {
      margin-top: 4px;
      color: #4b4c4d;
      text-align: center;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px;
    }
    .upload-success {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: 28px;
      color: #121212;
      text-align: center;
      font-size: 20px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
      letter-spacing: 0.15px;
    }
  }

  .file-info img {
    width: 40px;
    height: 40px;
    margin-right: 10px;
  }

  .upload-success {
    color: green;
    font-weight: bold;
    margin-left: 10px;
  }
`

export default UploadSuccess

const PdfSvg = (
  <svg xmlns='http://www.w3.org/2000/svg' width='64' height='64' viewBox='0 0 64 64' fill='none'>
    <path
      d='M8.89062 9.77788C8.89062 6.34144 11.6764 3.55566 15.1128 3.55566H32.0017H37.7795H38.168C39.8057 3.55566 41.3773 4.20132 42.5421 5.35258L53.2647 15.9511C54.4473 17.1201 55.1129 18.7137 55.1129 20.3765V32.0001V54.2223C55.1129 57.6588 52.3271 60.4446 48.8906 60.4446H15.1129C11.6764 60.4446 8.89062 57.6588 8.89062 54.2223V9.77788Z'
      fill='#FB6965'
    />
    <path
      d='M44.7963 30.1972C44.768 29.99 44.6025 29.73 44.4248 29.5553C43.916 29.0515 42.7976 28.7874 41.0936 28.763C40.4022 28.7557 39.6268 28.7892 38.8021 28.8617C37.6884 28.9597 36.5237 28.8079 35.6426 28.1198C35.4982 28.007 35.3601 27.8899 35.2307 27.7676C33.9137 26.5326 32.8002 24.8537 32.0524 22.9936C31.9319 22.6939 31.9191 22.3619 31.9937 22.0476V22.0476C32.0012 22.0165 32.007 21.988 32.0124 21.9564C32.0951 21.4735 32.7916 17.3283 32.594 15.8676C32.5657 15.6563 32.5455 15.5954 32.489 15.4288L32.4204 15.2501C32.2023 14.7422 31.7743 14.2059 31.104 14.2344L30.7083 14.2222H30.7002C29.9492 14.2222 29.3435 14.6081 29.182 15.181C28.7254 16.8809 29.1437 19.3782 29.9538 22.5845C30.0516 22.9716 30.0245 23.3803 29.8725 23.7495V23.7495C29.2224 25.3503 28.4068 26.9591 27.688 28.3771L27.5952 28.5599C26.9798 29.7686 26.4122 30.8305 25.8859 31.7607C25.6518 32.1744 25.3014 32.5093 24.8817 32.7324V32.7324C24.8333 32.7568 23.731 33.3459 23.4725 33.5003C21.28 34.8207 19.8264 36.3158 19.5841 37.5022C19.5074 37.8841 19.5639 38.3676 19.9556 38.591L20.5774 38.9079C20.848 39.042 21.1346 39.1111 21.4254 39.1111C22.7748 39.1111 24.3108 37.6508 26.3144 34.4267C26.9702 33.3714 27.9521 32.5424 29.1459 32.1982C30.9553 31.6765 32.8493 31.2257 34.6525 30.8895C35.7487 30.6851 36.8677 30.9217 37.8859 31.3763C39.7032 32.1877 41.6217 32.708 42.9429 32.708C43.2458 32.708 43.5082 32.6796 43.7182 32.6227C44.0452 32.5333 44.3198 32.3464 44.4894 32.0946C44.8205 31.5948 44.8851 30.9041 44.7963 30.1972ZM22.1156 37.1648C21.7719 37.495 21.4408 37.1965 21.7154 36.8069C22.2615 36.0321 23.1055 35.0855 24.167 34.2113V34.2113C24.2583 34.1371 24.3891 34.2456 24.3248 34.3439C23.4327 35.7069 22.7057 36.598 22.1156 37.1648ZM30.9627 15.0347C31.4634 15.0347 31.7501 16.3064 31.7743 17.5009V17.5009C31.7953 18.5353 30.8182 18.472 30.7681 17.4386C30.758 17.2293 30.7529 17.031 30.7527 16.8505C30.7527 16.8452 30.7527 16.8408 30.7527 16.8355C30.7516 16.7114 30.7411 15.0347 30.9627 15.0347ZM30.1646 30.5996C29.4298 30.8262 28.742 30.0238 29.1013 29.344V29.344V29.344C29.945 27.7403 32.4728 27.5584 33.8081 28.7835C33.9213 28.8873 34.0361 28.9888 34.1526 29.0881V29.0881C34.3449 29.2468 34.2684 29.5616 34.0244 29.6129C32.6372 29.9044 31.3551 30.2324 30.1646 30.5996ZM43.9362 31.2007C43.7828 31.2942 43.3427 31.3511 43.06 31.3511C42.4763 31.3511 41.8018 31.1798 40.9761 30.8768C40.5728 30.7287 40.668 30.1769 41.0976 30.1769V30.1769C42.0142 30.1769 42.2848 30.1728 43.1852 30.4044C44.0816 30.636 44.0937 31.1032 43.9362 31.2007Z'
      fill='white'
    />
    <path
      d='M36.3203 46.2222H40.8945V47.4934H37.6805V49.2215H40.5721V50.4928H37.6805V53.3333H36.3203V46.2222Z'
      fill='white'
    />
    <path
      d='M29.0703 46.2222H31.5085C32.0795 46.2222 32.5933 46.3082 33.0501 46.4804C33.5068 46.6525 33.8964 46.8975 34.2188 47.2153C34.5412 47.5265 34.7864 47.9006 34.9543 48.3376C35.1289 48.7746 35.2162 49.2547 35.2162 49.7777C35.2162 50.3074 35.1289 50.7908 34.9543 51.2278C34.7864 51.6648 34.5412 52.0388 34.2188 52.35C33.8964 52.6612 33.5068 52.9029 33.0501 53.0751C32.5933 53.2472 32.0795 53.3333 31.5085 53.3333H29.0703V46.2222ZM31.4481 52.062C31.8444 52.062 32.1937 52.0091 32.4959 51.9031C32.7982 51.7906 33.0501 51.635 33.2516 51.4363C33.4531 51.2377 33.6042 50.9993 33.705 50.7212C33.8057 50.4365 33.8561 50.122 33.8561 49.7777C33.8561 49.4334 33.8057 49.1222 33.705 48.8441C33.6042 48.5594 33.4531 48.3178 33.2516 48.1191C33.0501 47.9205 32.7982 47.7682 32.4959 47.6623C32.1937 47.5497 31.8444 47.4934 31.4481 47.4934H30.4305V52.062H31.4481Z'
      fill='white'
    />
    <path
      d='M23.1133 53.3333V46.2222H25.6523C25.9948 46.2222 26.3139 46.2784 26.6094 46.391C26.9117 46.4969 27.1736 46.6492 27.3953 46.8479C27.6169 47.0465 27.7916 47.2849 27.9192 47.5629C28.0468 47.8344 28.1106 48.1357 28.1106 48.4667C28.1106 48.8044 28.0468 49.1123 27.9192 49.3904C27.7916 49.6619 27.6169 49.8969 27.3953 50.0955C27.1736 50.2942 26.9117 50.4498 26.6094 50.5623C26.3139 50.6683 25.9948 50.7212 25.6523 50.7212H24.4734V53.3333H23.1133ZM25.6724 49.45C26.0284 49.45 26.2971 49.3507 26.4784 49.152C26.6598 48.9534 26.7505 48.725 26.7505 48.4667C26.7505 48.3409 26.727 48.2184 26.6799 48.0993C26.6396 47.9801 26.5725 47.8775 26.4784 47.7914C26.3911 47.6987 26.2803 47.6259 26.1459 47.5729C26.0116 47.5199 25.8538 47.4934 25.6724 47.4934H24.4734V49.45H25.6724Z'
      fill='white'
    />
  </svg>
)

const WordPdf = (
  <svg xmlns='http://www.w3.org/2000/svg' width='48' height='58' viewBox='0 0 48 58' fill='none'>
    <path
      d='M0.890625 6.77788C0.890625 3.34144 3.67641 0.555664 7.11285 0.555664H24.0017H29.7795H30.168C31.8057 0.555664 33.3773 1.20131 34.5421 2.35258L45.2647 12.9511C46.4473 14.1201 47.1129 15.7137 47.1129 17.3765V29.0001V51.2223C47.1129 54.6588 44.3271 57.4446 40.8906 57.4446H7.11286C3.67642 57.4446 0.890625 54.6588 0.890625 51.2223V6.77788Z'
      fill='#0066FF'
    />
  </svg>
)
